import './TwoFA.css';
import axios from 'axios';
import { toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';
import crssrImage from "../../images/icons/close-black.svg";
import { login, loginTemp as LT, logout } from '../../features/auth/authSlice';
import { getBrandLogo, getBrandLogoAlt } from "../../utils/brandLogo";

export default function TwoFA() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [qrcode, setQrcode] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [is2faVerify, setIs2faVerify] = useState(false);
  const [codeError, setCodeError] = useState(null);
  const { loginTemp } = useSelector((state) => state.auth);
  const { appSettings } = useSelector((state) => state.appSettings);
  const codeInputs = useRef([]);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [codeHasError, setCodeHasError] = useState(false);
  const [isLoadPage, setIsLoadPage] = useState(() => {
    // resolve synchronously to avoid blank flash
    if (localStorage.getItem("2faSkip") === "true") return false;
    if (!loginTemp) return false;
    const twoFactorEnabledAt = localStorage.getItem("twoFactorEnabledAt");
    if (twoFactorEnabledAt) {
      const enabledAtTimestamp = Number(twoFactorEnabledAt);
      if (Number.isFinite(enabledAtTimestamp)) {
        const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - enabledAtTimestamp <= oneWeekInMs) return false;
      }
    }
    return false;
  });
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef(null);

  const focusFirstCodeInput = () => {
    requestAnimationFrame(() => {
      codeInputs.current[0]?.focus();
    });
  };

  useEffect(() => {
    setIsLoadPage(true);

    if (localStorage.getItem("2faSkip") === "true") {
      dispatch(login(loginTemp));
      navigate('/user/dashboard');
      setIsLoadPage(false);
      return;
    }

    if (!loginTemp) {
      navigate('/');
      setIsLoadPage(false);
      return;
    }

    const twoFactorEnabledAt = localStorage.getItem("twoFactorEnabledAt");

    function checkTwoFactorStatus() {
      if (twoFactorEnabledAt && localStorage.getItem("2faSkip") !== "true") {
        const oneWeekInMs = 7 * 24 * 60 * 60 * 1000; // One week in milliseconds
        const enabledAtTimestamp = Number(twoFactorEnabledAt);

        if (!Number.isFinite(enabledAtTimestamp)) {
          setIsLoadPage(false);
          return;
        }

        const enabledAtDate = new Date(enabledAtTimestamp);
        const now = new Date();
    
        if (now - enabledAtDate > oneWeekInMs) {
          setIsLoadPage(false);
        } else {
          dispatch(login(loginTemp));
          navigate('/user/dashboard');
          setIsLoadPage(false);
        }
      } else {
        setIsLoadPage(false);
      }
    }

    checkTwoFactorStatus();
  }, [dispatch, navigate, loginTemp]);

  useEffect(() => {
    if (!loginTemp?._id) {
      return;
    }

    const fetchQr = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await axios.post('/api/user/generateQRCode', {
          userId: loginTemp._id,
        }, { signal: controller.signal });
        if (!controller.signal.aborted) {
        setQrcode(response.data);
        }
      } catch (error) {
        if (axios.isCancel(error) || error.name === 'AbortError') {
          return;
        }
        console.error(error);
        toast.error("Failed to generate QR code. Please try again.", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        global?.config?.slackMessage?.(error.toString());
      }
    };

    fetchQr();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loginTemp]);

  useEffect(() => {
    if (isLoadPage) {
      return;
    }

    if (loginTemp?.is_2fa_enabled === true || (is2faVerify && loginTemp?.is_2fa_enabled === false)) {
      focusFirstCodeInput();
    }
  }, [isLoadPage, loginTemp?.is_2fa_enabled, is2faVerify]);

  const cancelLogin = async () => {
    localStorage.clear();
    sessionStorage.clear();
    await dispatch(logout());
    navigate('/', { replace: true });
    dispatch(LT('sasa'));
  };

  const skipTwoFA = async () => {
    const tempUser = loginTemp;
    localStorage.setItem('2faSkip', 'true');
    await dispatch(login(tempUser));
    dispatch(LT('sasa'));
    navigate('/user/dashboard');
  };

  const enableTwoFA = async () => {
    if (!qrcode?.secret?.base32) {
      toast.error("QR code not available. Please refresh the page.", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    setIsLoading(true);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      await axios.post('/api/user/enableTwofa', {
        userId: loginTemp._id,
        secret: qrcode.secret.base32,
      }, { signal: controller.signal });
      setIs2faVerify(true);
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError') {
        return;
      }
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to enable 2FA. Please try again.", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      global?.config?.slackMessage?.(error.toString());
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const verifyEnableTwoFA = async () => {
    const isCodeEmpty = code.some((digit) => digit === '');
    if (isCodeEmpty) {
      setCodeHasError(true);
      return;
    }

    if (!qrcode?.secret?.base32) {
      toast.error("QR code not available. Please refresh the page.", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    setIsLoading(true);
    setCodeError(null);
    setCodeHasError(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await axios.post('/api/user/verify/enable/twofa', {
        userId: loginTemp._id,
        otpCode: code.join(''),
        secret: qrcode.secret.base32,
      }, { signal: controller.signal });
      if (response.data === true) {
        localStorage.removeItem("2faSkip");
        localStorage.setItem('twoFactorEnabled', 'true');
        localStorage.setItem('twoFactorEnabledAt', Date.now().toString());
        dispatch(login(loginTemp));
        navigate('/user/dashboard');
        closeVerify2faModal();
      } else {
        setCodeHasError(true);
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError') {
        return;
      }
      setCodeHasError(true);
      global?.config?.slackMessage?.(error.toString());
      console.error(error);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const closeVerify2faModal = () => {
    setCodeError(null);
    setCodeHasError(false);
    setCode(['', '', '', '', '', '']);
    setIs2faVerify(false);
  };

  const verifyTwoFA = async () => {
    const isCodeEmpty = code.some((digit) => digit === '');
    if (isCodeEmpty) {
      setCodeHasError(true);
      return;
    }

    if (!loginTemp?.twoFa_secret) {
      toast.error("2FA secret not available. Please contact support.", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    setIsLoading(true);
    setCodeError(null);
    setCodeHasError(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await axios.post('/api/user/verify-twofa', {
        otpCode: code.join(''),
        secret: loginTemp.twoFa_secret,
      }, { signal: controller.signal });

      if (response.data === true) {
        setCodeHasError(false);
        setCodeError(null);
        localStorage.setItem('twoFactorEnabled', 'true');
        localStorage.setItem('twoFactorEnabledAt', Date.now().toString());
        dispatch(login(loginTemp));
        navigate('/user/dashboard');
      } else {
        setCodeHasError(true);
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError') {
        return;
      }
      setCodeHasError(true);
      global?.config?.slackMessage?.(error.toString());
      console.error(error);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const showQrCode = () => {
    setShowQr(true);
  };

  const handleCodeChange = (index, value) => {
    const sanitizedValue = value.replace(/\D/g, '').slice(0, 1);
    const newCode = [...code];
    newCode[index] = sanitizedValue;
    setCode(newCode);

    if (codeHasError) {
      setCodeHasError(false);
    }
    if (codeError) {
      setCodeError(null);
    }

    // Move to the next input field
    if (sanitizedValue !== '' && index < code.length - 1) {
      codeInputs.current[index + 1].focus();
    }
  };

  const handleCodeKeyDown = (index, event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (loginTemp?.is_2fa_enabled) {
        verifyTwoFA();
      } else if (is2faVerify && loginTemp?.is_2fa_enabled === false) {
        verifyEnableTwoFA();
      }
      return;
    }

    if (event.key === 'Backspace' && index > 0 && code[index] === '') {
      // Move to the previous input field when backspace is pressed and the current field is empty
      codeInputs.current[index - 1].focus();
    }
  };

  const numberInputOnWheelPreventChange = (e) => {
    // Prevent the input value change
    e.target.blur()

    // Prevent the page/container scrolling
    e.stopPropagation()

    setTimeout(() => {
      e.target.focus()
    }, 0)
  }

  return (
    <>
      <div className="outer-twofactor">
        <div className="twofa-logo">
          <img src={getBrandLogo()} alt={getBrandLogoAlt()} />
        </div>
        {(!showQr && (loginTemp && loginTemp.is_2fa_enabled === false)) && (isLoadPage === false) && localStorage.getItem("2faSkip")!=="true" && (
          <div className="choosing-outer">
            <div className="choose-inner">
              <div className="content-two-factor">
                <h3>Consider adding a second method for two-factor authentication</h3>
                <p>
                  Having two methods helps make sure you can still access your account if your first method is ever
                  unavailable.
                </p>
              </div>
              <div className="two-factor-btns">
                <button className="add-method" onClick={showQrCode}>
                  Add second method
                </button>
                {(appSettings?.twoFa!==true) && (
                <button className="skip-btn" onClick={skipTwoFA}>
                  Skip for now
                </button>)}
              </div>
            </div>
          </div>
        )}

        {(loginTemp && loginTemp.is_2fa_enabled === true) && (isLoadPage === false) && (
          <div className="twoFAContainer">
            <button className="twofa-close-btn" type="button" onClick={cancelLogin} disabled={isLoading}>
              <img src={crssrImage} alt="Close" />
            </button>
            <div className="qr-code-title">
              Enter 6-digit code
            </div>
            <div className="qr-code-des">
              Enter the code from your two-factor authentication app
            </div>
            <div className="code-input-container">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(ref) => (codeInputs.current[index] = ref)}
                  type="text"
                  inputMode="numeric"
                  className={`code-input${codeHasError ? " error" : ""}`}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  maxLength={1}
                  onWheel={numberInputOnWheelPreventChange}
                  aria-invalid={codeHasError}
                  style={{
                    borderColor: codeHasError ? "#ef4444" : undefined,
                    borderWidth: codeHasError ? "2px" : undefined,
                  }}
                />
              ))}
            </div>
            {codeError && <p className="error" style={{ color: "red" }}>{codeError}</p>}
            <div className="qr-code-btn">
              <button type="submit" onClick={verifyTwoFA} disabled={isLoading}>
                {isLoading ? "Verifying..." : "Verify"}
              </button>
            </div>
          </div>
        )}

        {(showQr && !is2faVerify && loginTemp && loginTemp.is_2fa_enabled === false) && (isLoadPage === false) && (
          <div className="twoFAContainer">
            <button className="twofa-close-btn" type="button" onClick={cancelLogin} disabled={isLoading}>
              <img src={crssrImage} alt="Close" />
            </button>
            <div className="qr-title">
              <h2>Register Talenton</h2>
            </div>
            <div className="qr-des">
              <p>Scan this QR code with a Google Authenticator app.</p>
            </div>
            <div className="qr-image-sec">
              {qrcode?.data
                ? <img className="qrCodeImage" src={qrcode.data} alt="QR Code" />
                : <div className="twofa-qr-skeleton" />
              }
            </div>
            <div className="qr-btn">
              <button className="twofa-primary-btn" type="button" onClick={enableTwoFA} disabled={isLoading}>
                {isLoading ? "Enabling..." : "Enable 2FA"}
              </button>
            </div>
            <div className="qr-bottom-sec">
              <p>Once you connect your Talenton account to the security or authentication app, you’ll get a one-time verification code whenever you need it.</p>
            </div>
          </div>
        )}

        {(is2faVerify && loginTemp && loginTemp.is_2fa_enabled === false) && (isLoadPage === false) && (
          <div className="twoFAContainer">
            <button className="twofa-close-btn" type="button" onClick={closeVerify2faModal} disabled={isLoading}>
              <img src={crssrImage} alt="Close" />
            </button>
            <div className="qr-code-title">
              Enter 6-digit code
            </div>
            <div className="qr-code-des">
              Enter the code from your two-factor authentication app to enable 2FA
            </div>
            <div className="code-input-container">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(ref) => (codeInputs.current[index] = ref)}
                  type="text"
                  inputMode="numeric"
                  className={`code-input${codeHasError ? " error" : ""}`}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  maxLength={1}
                  onWheel={numberInputOnWheelPreventChange}
                  aria-invalid={codeHasError}
                  style={{
                    borderColor: codeHasError ? "#ef4444" : undefined,
                    borderWidth: codeHasError ? "2px" : undefined,
                  }}
                />
              ))}
            </div>
            {codeError && <p className="error two-fa-error" style={{ color: "red" }}>{codeError}</p>}
            <div className="qr-code-btn">
              <button type="submit" onClick={verifyEnableTwoFA} disabled={isLoading}>
                {isLoading ? "Verifying..." : "Verify"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}